import { useEffect, useState } from "react";
import { api, qs } from "../api.js";
import { t } from "../i18n.js";
import { useMe } from "../shared.jsx";
import { Confirm, Empty, ErrorBox, Field, Loader, Modal, Pager, useAction, useLoad } from "../ui.jsx";

export function People() {
  const me = useMe();
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [invite, setInvite] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => { const id = setTimeout(() => { setSearch(q); setPage(1); }, 350); return () => clearTimeout(id); }, [q]);
  const state = useLoad(() => api.get("/users" + qs({ q: search, role, status, page, page_size: 25 })), [search, role, status, page]);
  const { run } = useAction();
  const inviteLink = async (u) => {
    const r = await run(() => api.post(`/users/${u.id}/invite-link`, {}));
    setInvite({ user: u, ...r });
  };
  return (
    <div className="page">
      <header className="page-head"><h1>{t("people")}</h1>
        <button className="btn btn-primary" onClick={() => setEdit({})}>إضافة موظف</button></header>
      <div className="filters">
        <input type="search" placeholder="بحث بالاسم أو البريد أو المسمى" value={q} onChange={(e) => setQ(e.target.value)} aria-label="بحث" />
        <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} aria-label="الدور"><option value="">كل الأدوار</option>
          {Object.keys(t("role")).map((r) => <option key={r} value={r}>{t("role." + r)}</option>)}</select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="الحالة"><option value="">كل الحالات</option>
          {Object.keys(t("userStatus")).map((r) => <option key={r} value={r}>{t("userStatus." + r)}</option>)}</select>
      </div>
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<Empty title="لا يوجد موظفون مطابقون" />}>
        {(d) => (
          <>
            <div className="table-wrap"><table className="table">
              <thead><tr><th>الاسم</th><th>المسمى / القسم</th><th>الفرع</th><th>الدور</th><th>الحالة</th><th /></tr></thead>
              <tbody>{d.items.map((u) => (
                <tr key={u.id} className={u.status === "disabled" ? "muted" : ""}>
                  <td><b>{u.name}</b><br /><small dir="ltr">{u.email}</small></td>
                  <td>{[u.job_title, u.department].filter(Boolean).join(" / ") || "—"}</td><td>{u.branch || "—"}</td>
                  <td>{t("role." + u.role)}</td><td><span className={"pill pill-u-" + u.status}>{t("userStatus." + u.status)}</span></td>
                  <td className="row-actions">
                    <button className="btn btn-sm" onClick={() => api.get(`/users/${u.id}`).then(setEdit)}>تعديل</button>
                    {u.status !== "disabled" && <button className="btn btn-sm" onClick={() => inviteLink(u).catch(() => {})}>{u.status === "invited" ? "رابط التفعيل" : "رابط إعادة تعيين"}</button>}
                    {u.id !== me.id && (u.status === "disabled"
                      ? <button className="btn btn-sm" onClick={() => setConfirm({ u, action: "enable" })}>تفعيل</button>
                      : <button className="btn btn-sm btn-ghost-danger" onClick={() => setConfirm({ u, action: "disable" })}>تعطيل</button>)}
                  </td>
                </tr>))}</tbody>
            </table></div>
            <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
          </>
        )}
      </Loader>
      {edit && <UserForm user={edit.id ? edit : null} onClose={() => setEdit(null)}
                         onSaved={(r) => { setEdit(null); state.reload(); if (r?.invite_token) setInvite(r); }} />}
      {invite && <InviteLink data={invite} onClose={() => setInvite(null)} />}
      {confirm && <Confirm title={confirm.action === "disable" ? `تعطيل حساب ${confirm.u.name}` : `تفعيل حساب ${confirm.u.name}`}
        danger={confirm.action === "disable"} actionLabel={confirm.action === "disable" ? "تعطيل الحساب" : "تفعيل الحساب"}
        text={confirm.action === "disable" ? "يُمنع الدخول فورًا ويتوقف الوصول للبيانات والملفات، مع الاحتفاظ بسجل أعماله." : undefined}
        onConfirm={() => api.post(`/users/${confirm.u.id}/${confirm.action}`, {}).then(state.reload)} onClose={() => setConfirm(null)} />}
    </div>
  );
}

function InviteLink({ data, onClose }) {
  const url = `${location.origin}${location.pathname}#/invite?token=${encodeURIComponent(data.invite_token)}`;
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="رابط التفعيل" onClose={onClose}>
      <p>يظهر هذا الرابط مرة واحدة فقط. أرسله للموظف بنفسك عبر قناة آمنة — لا يرسل النظام أي رسائل تلقائيًا.</p>
      <p className="muted small">صالح حتى {new Date(data.invite_expires_at).toLocaleString("ar-LY", { timeZone: "Africa/Tripoli", numberingSystem: "latn" })}</p>
      <textarea readOnly rows={3} dir="ltr" value={url} onFocus={(e) => e.target.select()} aria-label="الرابط" />
      <div className="actions">
        <button className="btn btn-primary" onClick={() => navigator.clipboard?.writeText(url).then(() => setCopied(true))}>{copied ? "تم النسخ" : "نسخ الرابط"}</button>
        <button className="btn" onClick={onClose}>{t("close")}</button>
      </div>
    </Modal>
  );
}

function UserForm({ user, onClose, onSaved }) {
  const teams = useLoad(() => api.get("/teams?page_size=100"), []);
  const perms = useLoad(() => api.get("/permissions"), []);
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", job_title: user?.job_title || "",
    department: user?.department || "", branch: user?.branch || "", role: user?.role || "employee",
    team_ids: [], permissions: user?.permissions || [] });
  const { busy, error, run } = useAction();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const base = { name: f.name, email: f.email, phone: f.phone || null, job_title: f.job_title || null,
      department: f.department || null, branch: f.branch || null, role: f.role };
    const req = user
      ? api.patch(`/users/${user.id}`, { version: user.version, ...base })
        .then(() => api.put(`/users/${user.id}/permissions`, { permissions: f.permissions }))
      : api.post("/users", { ...base, team_ids: f.team_ids });
    run(() => req, user ? "تم حفظ بيانات الموظف" : "تم إنشاء الحساب").then(onSaved, () => {});
  };
  return (
    <Modal title={user ? `تعديل ${user.name}` : "إضافة موظف"} onClose={onClose} wide>
      <form className="form-grid" onSubmit={submit}>
        <Field label="الاسم الكامل"><input required minLength={2} value={f.name} onChange={set("name")} /></Field>
        <Field label="البريد الإلكتروني"><input required type="email" dir="ltr" value={f.email} onChange={set("email")} /></Field>
        <Field label="رقم الهاتف"><input type="tel" dir="ltr" value={f.phone} onChange={set("phone")} /></Field>
        <Field label="المسمى الوظيفي"><input value={f.job_title} onChange={set("job_title")} /></Field>
        <Field label="القسم"><input value={f.department} onChange={set("department")} /></Field>
        <Field label="الفرع"><select value={f.branch} onChange={set("branch")}><option value="">—</option>
          {["مصراتة", "طرابلس", "بنغازي"].map((b) => <option key={b}>{b}</option>)}</select></Field>
        <Field label="الدور"><select value={f.role} onChange={set("role")}>
          {Object.keys(t("role")).map((r) => <option key={r} value={r}>{t("role." + r)}</option>)}</select></Field>
        {!user && (
          <Field label="الفرق"><select multiple size={4} value={f.team_ids} onChange={(e) => setF({ ...f, team_ids: [...e.target.selectedOptions].map((o) => o.value) })}>
            {(teams.data?.items || []).map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}</select></Field>
        )}
        {user && (
          <fieldset className="field span-2"><legend className="field-label">صلاحيات إضافية</legend>
            {Object.entries(perms.data?.permissions || {}).map(([k, label]) => (
              <label className="check" key={k}><input type="checkbox" checked={f.permissions.includes(k)}
                onChange={(e) => setF({ ...f, permissions: e.target.checked ? [...f.permissions, k] : f.permissions.filter((x) => x !== k) })} />{label}</label>))}
          </fieldset>
        )}
        {!user && <p className="span-2 muted small">يُنشأ الحساب بحالة «مدعو» ويظهر لك رابط التفعيل لإرساله للموظف.</p>}
        <div className="span-2"><ErrorBox error={error} /></div>
        <div className="actions span-2"><button className="btn btn-primary" disabled={busy}>{busy ? "جارٍ الحفظ…" : "حفظ"}</button>
          <button type="button" className="btn" onClick={onClose}>{t("cancel")}</button></div>
      </form>
    </Modal>
  );
}
